import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/lib/cn';

import { AUTO_ICON, THEME_ICONS, THEME_KEYS, THEME_LABELS } from './themeKeys';
import { useTheme } from './useTheme';

export default function ThemeSwitcher() {
  const { resolved, scheduledNow, isAuto, manual, setManual, setAuto } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const ActiveIcon = isAuto ? AUTO_ICON : THEME_ICONS[manual ?? resolved];
  const activeLabel = isAuto ? 'Auto' : THEME_LABELS[manual ?? resolved];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Theme: ${activeLabel}. Change theme.`}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-(--border-subtle) bg-(--bg-surface) px-3.5 py-1.5 text-sm font-medium text-(--text-secondary) transition-colors',
          'hover:border-(--border-default) hover:bg-(--bg-surface-hover) hover:text-(--text-primary)',
          open && 'border-(--border-default) bg-(--bg-surface-strong) text-(--text-primary)',
        )}
      >
        <ActiveIcon aria-hidden className="h-4 w-4" />
        <span>{activeLabel}</span>
        {isAuto && (
          <span className="text-xs text-(--text-tertiary)" aria-hidden>
            · {THEME_LABELS[resolved]}
          </span>
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Choose theme"
          className={cn(
            'absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border shadow-(--shadow-modal)',
            'border-(--border-default) bg-(--bg-surface-strong) backdrop-blur',
          )}
        >
          <ThemeMenuItem
            icon={<AUTO_ICON aria-hidden className="h-4 w-4" />}
            label="Auto"
            description={`Now: ${THEME_LABELS[scheduledNow]}`}
            checked={isAuto}
            onSelect={() => {
              setAuto();
              setOpen(false);
            }}
          />
          <div className="border-t border-(--border-subtle)" />
          {THEME_KEYS.map((key) => {
            const Icon = THEME_ICONS[key];
            const checked = !isAuto && manual === key;
            return (
              <ThemeMenuItem
                key={key}
                icon={<Icon aria-hidden className="h-4 w-4" />}
                label={THEME_LABELS[key]}
                description={scheduledNow === key && isAuto ? 'Current schedule' : undefined}
                checked={checked}
                onSelect={() => {
                  setManual(key);
                  setOpen(false);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

interface ThemeMenuItemProps {
  icon: React.ReactNode;
  label: string;
  description?: string;
  checked: boolean;
  onSelect: () => void;
}

function ThemeMenuItem({ icon, label, description, checked, onSelect }: ThemeMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
        'hover:bg-(--bg-surface-hover) focus-visible:bg-(--bg-surface-hover)',
        checked ? 'text-(--text-primary)' : 'text-(--text-secondary)',
      )}
    >
      <span className="text-(--accent-primary)">{icon}</span>
      <span className="flex-1">
        <span className="block">{label}</span>
        {description && <span className="block text-xs text-(--text-tertiary)">{description}</span>}
      </span>
      <span aria-hidden className={cn('h-2 w-2 rounded-full', checked ? 'bg-(--accent-primary)' : 'bg-transparent')} />
    </button>
  );
}
